// src\app\[lang]\components\BooksWidget.tsx
'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { GetBooksQuery } from '../../../gql/gql-generated';
import { getImageUrl } from '../../utils/imageHelpers';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronLeftIcon as ArrowBackIos, ChevronRightIcon as ArrowForwardIos } from '@heroicons/react/24/solid';

// Type imports only - no runtime cost
import type { Swiper as SwiperClass } from 'swiper/types';

interface BooksWidgetProps {
  books: GetBooksQuery;
  lang: string;
}

const BooksWidget: React.FC<BooksWidgetProps> = ({ books, lang }) => {
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null); // null = not yet checked
  const [SwiperComponents, setSwiperComponents] = useState<any>(null);

  // Check if we're on desktop and dynamically load Swiper only then
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleMediaChange = async (e: MediaQueryListEvent | MediaQueryList) => {
      const isDesktopView = e.matches;
      setIsDesktop(isDesktopView);

      // Only load Swiper on desktop
      if (isDesktopView && !SwiperComponents) {
        // Load Swiper CSS dynamically
        // @ts-ignore - CSS imports don't have type declarations
        await import('swiper/swiper-bundle.css');

        const [
          { Swiper, SwiperSlide },
          { Navigation, EffectFade },
        ] = await Promise.all([
          import('swiper/react'),
          import('swiper/modules'),
        ]);

        setSwiperComponents({ Swiper, SwiperSlide, Navigation, EffectFade });
      }
    };

    // Check initial state
    handleMediaChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, [SwiperComponents]);

  const handlePrevious = useCallback(
    (event: React.MouseEvent) => {
      event?.stopPropagation();
      swiperRef?.slidePrev();
    },
    [swiperRef]
  );

  const handleNext = useCallback(
    (event: React.MouseEvent) => {
      event?.stopPropagation();
      swiperRef?.slideNext();
    },
    [swiperRef]
  );

  const booksList = books?.books?.nodes?.filter(Boolean) || [];
  const shouldLoop = booksList.length > 2;

  // Reusable book item component WITHOUT its own hover container
  const BookItem = ({
    book,
    tabIndex = 0,
  }: {
    book: any;
    tabIndex?: number;
  }) => (
    <Link
      href={book.uri || '#'}
      className="inline-block w-full"
      tabIndex={tabIndex}
      prefetch={false}
    >
      <div className="w-full flex flex-col bg-transparent cursor-pointer">
        {/* Top section: Image + Title side by side */}
        <div className="flex items-start">
          {/* Image Container */}
          <div className="w-1/3 relative flex-shrink-0 mr-3">
            <Image
              src={getImageUrl(book.featuredImage?.node, 128)}
              alt={book.featuredImage?.node?.altText || book.title || ''}
              width={0}
              height={0}
              sizes="56px"
              style={{ width: '100%', height: 'auto' }}
              loading="lazy"
              placeholder="blur"
              blurDataURL={
                book.featuredImage?.node?.thumbhash
                  ? `data:image/svg+xml;base64,${book.featuredImage.node.thumbhash}`
                  : 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD...'
              }
            />
          </div>
          {/* Title Container */}
          <div className="flex-1">
            <span className="font-helvetica text-sm text-grey-900 font-bold m-0 hyperlink block">
              {book.title}
            </span>
          </div>
        </div>

        {/* Bottom section: Subheading spans full width */}
        {book.bookDetails?.subheading && (
          <div className="w-full mt-2">
            <p className="font-helvetica text-xs text-gray-900 m-0 leading-relaxed">
              {book.bookDetails.subheading}
            </p>
          </div>
        )}
      </div>
    </Link>
  );

  const { Swiper, SwiperSlide, Navigation, EffectFade } = SwiperComponents || {};

  return (
    <div className="books-widget relative mb-4">
      {/* SINGLE CONTAINER with hover effect for Books header + content */}
      <div className="group hover:bg-black hover:bg-opacity-[0.04] transition-colors duration-200 rounded-sm pt-4 pb-2 pl-2 pr-2 -m-2">
        {/* Books Header */}
        <div className="mb-2 flex justify-between items-start">
          <Link
            href={`/${lang}/book`}
            className="inline-block hyperlink"
            prefetch={false}
            passHref
          >
            <h2 className="font-telegrafico text-communist-red mb-0 text-xl font-light leading-none -mt-1">
              Books
            </h2>
          </Link>
        </div>

        {/* Book Content */}
        {booksList.length === 0 && (
          <div className="text-center text-sm text-gray-500">
            No books found.
          </div>
        )}

        {booksList.length === 1 && <BookItem book={booksList[0]} />}

        {booksList.length === 2 && (
          <div className="flex gap-4">
            {booksList.map((book) => (
              <div key={book.id} className="flex-1">
                <BookItem book={book} />
              </div>
            ))}
          </div>
        )}

        {/* Only render Swiper on desktop when components are loaded */}
        {shouldLoop && isDesktop === true && Swiper && (
          <Swiper
            key={booksList.length}
            className="p-0"
            onSwiper={setSwiperRef}
            spaceBetween={30}
            slidesPerView={1}
            pagination={{ clickable: true }}
            navigation={false}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            modules={[Navigation, EffectFade]}
            loop={true}
            allowTouchMove={false}
          >
            {booksList.map((book) => (
              <SwiperSlide key={book.id}>
                {({ isActive }: { isActive: boolean }) => (
                  <BookItem book={book} tabIndex={isActive ? 0 : -1} />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        {/* Fallback for mobile when there are more than 2 books - show first book only */}
        {shouldLoop && isDesktop === false && booksList.length > 0 && (
          <BookItem book={booksList[0]} />
        )}
      </div>
      {/* END of hover container */}

      {/* Navigation buttons - OUTSIDE the hover container - only show on desktop with Swiper */}
      {shouldLoop && isDesktop === true && Swiper && (
        <div className="mt-2 flex justify-center items-center">
          <div className="flex w-full max-w-xs">
            <button
              onClick={handlePrevious}
              className="flex-1 flex justify-center items-center p-2 rounded-l-full border-0  bg-transparent transition-colors hover:bg-gray-400/10"
              aria-label="Previous book"
            >
              <ArrowBackIos className="w-5 h-5 text-communist-red" />
            </button>
            <button
              onClick={handleNext}
              className="flex-1 flex justify-center items-center p-2 rounded-r-full border-0  bg-transparent transition-colors hover:bg-gray-400/10"
              aria-label="Next book"
            >
              <ArrowForwardIos className="w-5 h-5 text-communist-red" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(BooksWidget);
